import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram, Phone, Mail, Lock } from "lucide-react";
import { useSiteData } from "@/hooks/useSite";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/chi-sono", label: "Chi sono" },
  { to: "/servizi", label: "Servizi" },
  { to: "/il-metodo", label: "Il metodo" },
  { to: "/contatti", label: "Contatti" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { settings } = useSiteData();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 bg-ivory/85 backdrop-blur-xl border-b ${
          scrolled ? "border-taupe/30 shadow-[0_1px_0_rgba(107,92,80,0.06)]" : "border-taupe/15"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-[1440px] mx-auto flex items-center justify-between px-5 lg:px-12 h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-3 shrink-0" aria-label="K Nutrition — Home" data-testid="nav-logo">
            <img src={settings?.brand?.logo || "/images/logo-transparent.png"} alt="K Nutrition" className="h-11 lg:h-14 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Navigazione principale">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `link-underline text-sm uppercase tracking-widest2 font-medium transition-colors duration-300 ${
                    isActive ? "text-deeptaupe" : "text-charcoal/75 hover:text-deeptaupe"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/prenota"
              data-testid="nav-cta-button"
              className="ml-2 inline-flex items-center rounded-full bg-deeptaupe text-ivory text-sm uppercase tracking-widest2 font-medium px-6 py-3 transition-all duration-300 hover:-translate-y-0.5 hover:bg-charcoal"
            >
              Prenota
            </Link>
            <Link
              to="/admin"
              data-testid="nav-admin-button"
              title="Area riservata amministratore"
              aria-label="Area riservata amministratore"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-taupe/40 text-charcoal/50 transition-all duration-300 hover:bg-peach hover:text-deeptaupe"
            >
              <Lock size={15} strokeWidth={1.5} />
            </Link>
          </nav>

          <button
            className="lg:hidden p-2 -mr-2 text-charcoal"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-40 lg:hidden bg-peach flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
          >
            <div className="flex-1 flex flex-col justify-center px-8 pt-20">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5 }}
                >
                  <NavLink
                    to={l.to}
                    className={({ isActive }) =>
                      `block font-display text-4xl py-3 border-b border-taupe/25 ${
                        isActive ? "text-deeptaupe italic" : "text-charcoal"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.42, duration: 0.5 }}
                className="pt-6"
              >
                <Link
                  to="/prenota"
                  data-testid="mobile-cta-button"
                  className="inline-flex items-center justify-center w-full rounded-full bg-deeptaupe text-ivory text-sm uppercase tracking-widest2 font-medium px-6 py-4"
                >
                  Prenota una consulenza
                </Link>
              </motion.div>
            </div>
            <div className="px-8 pb-10 flex items-center gap-6 text-charcoal/70">
              <a href="tel:+393793165184" aria-label="Chiama K Nutrition" data-testid="mobile-menu-phone" className="p-2"><Phone size={20} strokeWidth={1.5} /></a>
              <a href="mailto:knutrition.studio@gmail.com" aria-label="Email K Nutrition" data-testid="mobile-menu-email" className="p-2"><Mail size={20} strokeWidth={1.5} /></a>
              <a href="https://www.instagram.com/knutritionstudio/" target="_blank" rel="noopener noreferrer" aria-label="Instagram K Nutrition" data-testid="mobile-menu-instagram" className="p-2"><Instagram size={20} strokeWidth={1.5} /></a>
              <Link to="/admin" aria-label="Area riservata amministratore" title="Area riservata amministratore" data-testid="mobile-menu-admin" className="p-2 text-charcoal/50"><Lock size={20} strokeWidth={1.5} /></Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
